/**
 * useBooxSocket — unified WebSocket hook for boox platform real-time events.
 *
 * Features:
 *  - Auto-connects on mount with JWT from auth store
 *  - Reconnects with exponential backoff (max 30s)
 *  - Sends ping every 60s to keep presence alive
 *  - Exposes publish() to subscribe to additional channels (threads, jobs)
 *  - Typed event listeners via on() / off()
 *
 * Usage:
 *   const { isConnected, subscribe, on, off } = useBooxSocket();
 *
 *   // Listen for new messages in current thread
 *   useEffect(() => {
 *     on('message.new', (payload) => setMessages(prev => [...prev, payload]));
 *     subscribe(`boox:thread:${threadId}`);
 *     return () => off('message.new');
 *   }, [threadId]);
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { getToken } from '@/lib/api';

// ── Types (mirrors backend ws/types.ts) ──────────────────────────────────────

export type WsEventType =
  | 'message.new' | 'message.updated' | 'message.deleted' | 'thread.updated'
  | 'typing.start' | 'typing.stop'
  | 'network.message.new' | 'network.notification.new'
  | 'network.connection.request' | 'network.connection.accepted'
  | 'network.channel.post.new' | 'network.job_thread.invited'
  | 'presence.update' | 'presence.list'
  | 'ws.welcome' | 'ws.error' | 'ws.pong';

export interface WsEvent<T = unknown> {
  v:       number;
  type:    WsEventType;
  payload: T;
  ts:      number;
}

type EventHandler<T = unknown> = (payload: T, event: WsEvent<T>) => void;

// ── Constants ─────────────────────────────────────────────────────────────────

const _apiBase = (import.meta.env?.VITE_API_BASE_URL as string | undefined) ?? 'https://hub.boox.app';
// Derive WS URL from API base: https://hub.boox.app → wss://hub.boox.app/ws
const WS_BASE_URL = (import.meta.env?.VITE_WS_URL as string | undefined)
  ?? _apiBase.replace(/^http/, 'ws').replace(/\/api\/v1\/?$/, '') + '/ws';
const PING_INTERVAL_MS  = 60_000; // 60 seconds
const INITIAL_BACKOFF   = 1_000;  // 1 second
const MAX_BACKOFF       = 30_000; // 30 seconds

function shouldSkipRemoteSocket(): boolean {
  if (typeof window === 'undefined') return false;
  const host = window.location.hostname;
  const isLocalHost = host === 'localhost' || host === '127.0.0.1';
  const usesDefaultHub = !import.meta.env?.VITE_WS_URL && !(import.meta.env?.VITE_API_BASE_URL) && WS_BASE_URL.includes('hub.boox.app/ws');
  return isLocalHost && usesDefaultHub;
}

// ── Singleton socket — shared across all hook instances ───────────────────────

let globalSocket:      WebSocket | null = null;
let globalListeners:   Map<WsEventType, Set<EventHandler>> = new Map();
let reconnectTimer:    ReturnType<typeof setTimeout> | null = null;
let pingTimer:         ReturnType<typeof setInterval> | null = null;
let backoffMs          = INITIAL_BACKOFF;
let subscribedChannels = new Set<string>();
let isConnecting       = false;
let currentToken:      string | null = null;
let connectionCount    = 0; // how many hook instances are mounted

function getOrCreateSocket(token: string): WebSocket {
  if (shouldSkipRemoteSocket()) {
    throw new Error('Remote websocket disabled for localhost without explicit WS configuration');
  }

  if (globalSocket && (globalSocket.readyState === WebSocket.CONNECTING || globalSocket.readyState === WebSocket.OPEN)) {
    return globalSocket;
  }

  isConnecting = true;
  currentToken = token;

  const url = `${WS_BASE_URL}?token=${encodeURIComponent(token)}`;
  const ws   = new WebSocket(url);
  globalSocket = ws;

  ws.onopen = () => {
    isConnecting = false;
    backoffMs    = INITIAL_BACKOFF;

    // Re-subscribe to all channels after reconnect
    for (const channel of subscribedChannels) {
      ws.send(JSON.stringify({ type: 'subscribe', payload: { channel } }));
    }

    // Start ping
    if (pingTimer) clearInterval(pingTimer);
    pingTimer = setInterval(() => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, PING_INTERVAL_MS);

    notifyStateListeners();
  };

  ws.onmessage = (event) => {
    try {
      const msg = JSON.parse(event.data as string) as WsEvent;
      const handlers = globalListeners.get(msg.type);
      if (handlers) {
        for (const handler of handlers) {
          try { handler(msg.payload, msg); } catch { /* ignore */ }
        }
      }
    } catch { /* ignore malformed */ }
  };

  ws.onclose = (event) => {
    isConnecting = false;
    if (pingTimer) { clearInterval(pingTimer); pingTimer = null; }

    // Don't reconnect on intentional close (code 4001 = auth failure)
    if (event.code === 4001) {
      notifyStateListeners();
      return;
    }

    // Exponential backoff
    if (connectionCount > 0) {
      reconnectTimer = setTimeout(() => {
        if (currentToken && connectionCount > 0) {
          getOrCreateSocket(currentToken);
          notifyStateListeners();
        }
      }, backoffMs);
      backoffMs = Math.min(backoffMs * 2, MAX_BACKOFF);
    }

    notifyStateListeners();
  };

  ws.onerror = () => { /* onclose will fire after */ };

  return ws;
}

// ── State listeners — updates connection status for hook consumers ────────────
let stateListeners = new Set<() => void>();
function notifyStateListeners() {
  for (const fn of stateListeners) fn();
}

function getIsConnected() {
  return globalSocket?.readyState === WebSocket.OPEN;
}

// ── The hook ──────────────────────────────────────────────────────────────────

export function useBooxSocket() {
  const [isConnected, setIsConnected] = useState(getIsConnected);

  // Track state changes
  useEffect(() => {
    const update = () => setIsConnected(getIsConnected());
    stateListeners.add(update);

    if (shouldSkipRemoteSocket()) {
      update();
      return () => {
        stateListeners.delete(update);
      };
    }

    // Connect on mount if we have a token
    const token = getToken();
    if (token) {
      connectionCount++;
      getOrCreateSocket(token);
      notifyStateListeners();
    }

    return () => {
      stateListeners.delete(update);
      connectionCount--;

      // Close socket if no more consumers
      if (connectionCount <= 0) {
        connectionCount = 0;
        if (reconnectTimer) { clearTimeout(reconnectTimer); reconnectTimer = null; }
        if (pingTimer)      { clearInterval(pingTimer);     pingTimer      = null; }
        globalSocket?.close(1000, 'All consumers unmounted');
        globalSocket = null;
      }
    };
  }, []); // token read via getToken() inside effect, no dep needed

  /** Subscribe to an additional Redis channel (thread, job, etc.) */
  const subscribe = useCallback((channel: string) => {
    subscribedChannels.add(channel);
    if (globalSocket?.readyState === WebSocket.OPEN) {
      globalSocket.send(JSON.stringify({ type: 'subscribe', payload: { channel } }));
    }
  }, []);

  /** Register an event listener */
  const on = useCallback(<T>(type: WsEventType, handler: EventHandler<T>) => {
    if (!globalListeners.has(type)) {
      globalListeners.set(type, new Set());
    }
    globalListeners.get(type)!.add(handler as EventHandler);
  }, []);

  /** Remove an event listener */
  const off = useCallback(<T>(type: WsEventType, handler: EventHandler<T>) => {
    globalListeners.get(type)?.delete(handler as EventHandler);
  }, []);

  /** Send typing indicator */
  const sendTyping = useCallback((threadId: string, isTyping: boolean) => {
    if (globalSocket?.readyState === WebSocket.OPEN) {
      globalSocket.send(JSON.stringify({
        type:    isTyping ? 'typing.start' : 'typing.stop',
        payload: { threadId },
      }));
    }
  }, []);

  return { isConnected, subscribe, on, off, sendTyping };
}

/**
 * Convenience hook: subscribe to a specific thread and return new messages.
 * Automatically unsubscribes on unmount.
 */
export function useThreadSocket(threadId: string | null | undefined) {
  const { on, off, subscribe, isConnected } = useBooxSocket();

  useEffect(() => {
    if (!threadId) return;
    const channel = `boox:thread:${threadId}`;
    subscribe(channel);
    return () => {
      subscribedChannels.delete(channel);
    };
  }, [threadId, subscribe]);

  return { on, off, isConnected };
}

/**
 * Convenience hook: returns the total unread network notification count.
 * Includes: platform notifications, connection requests, DMs, and channel posts.
 * Used by NotificationBell to show the combined badge total.
 */
export function useNetworkNotificationBadge() {
  const { on, off } = useBooxSocket();
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const handler = () => setUnreadCount((n) => n + 1);
    on('network.notification.new', handler);
    on('network.connection.request', handler);
    on('network.message.new', handler);
    on('network.channel.post.new', handler);
    return () => {
      off('network.notification.new', handler);
      off('network.connection.request', handler);
      off('network.message.new', handler);
      off('network.channel.post.new', handler);
    };
  }, [on, off]);

  return { unreadCount, clear: () => setUnreadCount(0) };
}

/**
 * Convenience hook: returns per-feature unread counts for sidebar nav badges.
 * Each counter increments independently when the relevant WS event fires.
 */
export function useNetworkItemBadges() {
  const { on, off } = useBooxSocket();
  const [messages, setMessages]      = useState(0);
  const [connections, setConnections] = useState(0);
  const [channels, setChannels]      = useState(0);

  useEffect(() => {
    const onMsg  = () => setMessages((n) => n + 1);
    const onConn = () => setConnections((n) => n + 1);
    const onChan = () => setChannels((n) => n + 1);
    on('network.message.new', onMsg);
    on('network.connection.request', onConn);
    on('network.channel.post.new', onChan);
    return () => {
      off('network.message.new', onMsg);
      off('network.connection.request', onConn);
      off('network.channel.post.new', onChan);
    };
  }, [on, off]);

  return { messages, connections, channels };
}
